import { ScreenBase } from "@frui.ts/screens/dist";
import { action, computed } from "mobx";
import ChargeTypesService from "services/chargeTypesService";
import EnumService from "services/enumService";
import LocalizationService from "services/localizationService";
import { PaymentType } from "models/payment";
import { interfaces } from "inversify";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import TimeService from "services/timeService";

export default class OrderSummaryViewModel extends ScreenBase {
  constructor(
    public chargeOrder: ChargeOrder,
    public timeService: TimeService,
    public localizationService: LocalizationService,
    public chargeService: ChargeTypesService,
    private enumService: EnumService
  ) {
    super();
  }

  chargeTa = (code: string) => this.localizationService.translateAttribute("chargeType", code);

  fuelTa = (code: string) => this.localizationService.translateAttribute("fuelType", code);

  summaryTg = (code: string) => this.localizationService.translateGeneral(`eshop.order.summary.${code}`);

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get vehicleInfo() {
    if (!(this.chargeOrder.charges && this.chargeOrder.charges.length > 0)) {
      return "";
    }

    const item = this.chargeOrder.charges[0];
    if (!item) {
      return "";
    }

    const country = this.enumService.values("countries").find(country => country.id === item.countryId);
    if (!country || !country.code) {
      return "";
    }

    return `${country.code} – ${
      item.licensePlates && item.licensePlates.length > 0 ? item.licensePlates[0] : item.licensePlate ? item.licensePlate : ""
    }`;
  }

  @computed
  get authorizationCode() {
    if (!this.chargeOrder.paymentType || this.chargeOrder.paymentType === PaymentType.Transfer) {
      return undefined;
    }

    if (!(this.chargeOrder.charges && this.chargeOrder.charges.length > 0)) {
      return undefined;
    }

    const item = this.chargeOrder.charges[0];
    if (!item) {
      return undefined;
    }

    return item.authCode;
  }

  @computed
  get displayAuthCode() {
    return (
      this.chargeOrder._status !== ChargeOrderStatus.Deferred &&
      this.authorizationCode &&
      this.chargeOrder.paymentType !== PaymentType.Transfer
    );
  }

  @computed
  get chargeType() {
    if (!(this.chargeOrder.charges && this.chargeOrder.charges.length > 0)) {
      return this.chargeTa("undefined");
    }

    const item = this.chargeOrder.charges[0];
    if (!item) {
      return this.chargeTa("undefined");
    }

    const chargeType = this.chargeService.getPriceListItemChargeType(item.priceListItemId);
    return chargeType ? chargeType.description : this.chargeTa("undefined");
  }

  @action.bound
  openRedirectLink() {
    window.open(this.chargeOrder._proposedAfterPurchaseRedirect?.redirectUrl ?? "https://edalnice.cz", "_self");
  }

  @computed
  get fuelType() {
    if (!(this.chargeOrder.charges && this.chargeOrder.charges.length > 0)) {
      return this.fuelTa("undefined");
    }

    const item = this.chargeOrder.charges[0];
    if (!item) {
      return this.fuelTa("undefined");
    }

    const fuelType = this.chargeService.getPriceListItemFuelType(item.priceListItemId);
    return fuelType ? this.fuelTa(fuelType) : this.fuelTa("undefined");
  }

  @computed
  get validSince() {
    if (!(this.chargeOrder.charges && this.chargeOrder.charges.length > 0)) {
      return null;
    }

    const item = this.chargeOrder.charges[0];
    if (!item || !item.validity?.validSinceDate) {
      return null;
    }

    return item.validity?.validSinceDate;
  }

  @computed
  get email() {
    return this.chargeOrder.emailAddress;
  }

  @computed
  get validUntil() {
    if (!(this.chargeOrder.charges && this.chargeOrder.charges.length > 0)) {
      return null;
    }

    const item = this.chargeOrder.charges[0];
    if (!item || !item.validity?._validUntilDate) {
      return null;
    }

    return item.validity?._validUntilDate;
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrder: ChargeOrder) =>
      new OrderSummaryViewModel(
        chargeOrder,
        container.get(TimeService),
        container.get(LocalizationService),
        container.get(ChargeTypesService),
        container.get(EnumService)
      );
  }
}
